import { useState, useEffect, useRef } from "react";
import { Bell, X, AlertTriangle, AlertCircle, Info, CheckCircle, Trash2, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function NotificationCenter({ notifications, isConnected, onClear }) {
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case "interference_alert":
        return <AlertCircle className="w-4 h-4 text-[#FF3B30]" />;
      case "warning_alert":
        return <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />;
      case "system_update":
        return <Info className="w-4 h-4 text-[#3B82F6]" />;
      case "test":
        return <CheckCircle className="w-4 h-4 text-[#34C759]" />;
      default:
        return <Bell className="w-4 h-4 text-[#71717A]" />;
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case "interference_alert":
        return "border-l-[#FF3B30] bg-[#FF3B30]/5";
      case "warning_alert":
        return "border-l-[#FFCC00] bg-[#FFCC00]/5";
      case "system_update":
        return "border-l-[#3B82F6] bg-[#3B82F6]/5";
      case "test":
        return "border-l-[#34C759] bg-[#34C759]/5";
      default:
        return "border-l-[#71717A]";
    }
  };

  // Get frequency color based on alert type
  const getFrequencyColor = (type) => {
    switch (type) {
      case "interference_alert":
        return "text-[#FF3B30]"; // Red for critical
      case "warning_alert":
        return "text-[#FFCC00]"; // Yellow/Orange for warning
      case "system_update":
        return "text-[#3B82F6]"; // Blue for info
      case "test":
        return "text-[#34C759]"; // Green for test/ok
      default:
        return "text-[#71717A]"; // Gray default
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-sm hover:bg-white/5 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className={`w-5 h-5 ${isConnected ? "text-[#F4F4F5]" : "text-[#71717A]"}`} />
          
          {/* Connection indicator */}
          <span 
            className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
              isConnected ? "bg-[#34C759]" : "bg-[#FF3B30]"
            }`}
            title={isConnected ? "Conectado" : "Desconectado"}
          />
          
          {/* Unread badge */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[#FF3B30] text-white text-xs font-bold rounded-full px-1">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-80 md:w-96 p-0 bg-[#111111] border-white/[0.08]"
        align="end"
        sideOffset={8}
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-[#F4F4F5]">Notificaciones</h3>
            <span 
              className={`text-xs px-2 py-0.5 rounded-full ${
                isConnected 
                  ? "bg-[#34C759]/20 text-[#34C759]" 
                  : "bg-[#FF3B30]/20 text-[#FF3B30]"
              }`}
            >
              {isConnected ? "En vivo" : "Desconectado"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-1.5 rounded hover:bg-white/5"
              title={soundEnabled ? "Silenciar" : "Activar sonido"}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-[#71717A]" />
              ) : (
                <VolumeX className="w-4 h-4 text-[#71717A]" />
              )}
            </button>
            {notifications.length > 0 && (
              <button
                onClick={onClear}
                className="p-1.5 rounded hover:bg-white/5"
                title="Limpiar todo"
              >
                <Trash2 className="w-4 h-4 text-[#71717A]" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded hover:bg-white/5"
            >
              <X className="w-4 h-4 text-[#71717A]" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[350px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-10 h-10 mx-auto mb-3 text-[#71717A]/30" />
              <p className="text-[#71717A] text-sm">Sin notificaciones</p>
              <p className="text-[#71717A]/60 text-xs mt-1">
                Las alertas RF aparecerán aquí en tiempo real
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {notifications.map((notification, index) => (
                <div
                  key={notification.timestamp + index}
                  className={`p-4 border-l-2 ${getNotificationBg(notification.type)} hover:bg-white/[0.02] transition-colors`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F4F4F5]">
                        {notification.data?.message || "Notificación"}
                      </p>
                      {notification.data?.system_name && (
                        <p className="text-xs text-[#71717A] mt-1">
                          Sistema: {notification.data.system_name}
                        </p>
                      )}
                      {notification.data?.frequency && (
                        <p className={`text-xs font-mono font-semibold mt-1 ${getFrequencyColor(notification.type)}`}>
                          ⚡ {notification.data.frequency} MHz
                        </p>
                      )}
                      <p className="text-xs text-[#71717A]/60 mt-2">
                        {notification.timestamp 
                          ? format(new Date(notification.timestamp), "HH:mm:ss", { locale: es })
                          : "Ahora"
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-white/[0.08] text-center">
            <p className="text-xs text-[#71717A]">
              {notifications.length} notificación{notifications.length !== 1 ? "es" : ""}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationCenter;
